import { lighten, Theme, Typography } from '@mui/material';
import React, { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { SupplyChainItem, SupplyChainItemType } from '../supplychain-types';
import SupplyChainCoverage from './SupplyChainCoverage';
import SupplyChainElementConnectors from './SupplyChainElementConnectors';
import SupplyChainHeader from './SupplyChainHeader';
import SupplyChainIssues from './SupplyChainIssues';
import SupplyChainToolConnectors from './SupplyChainToolConnectors';

const SupplyChain: FC<React.PropsWithChildren<SupplyChainProps>> = ({
  items,
  haveCoverage = true,
}) => {
  const { classes, cx } = useStyles();
  if (items === undefined) {
    return <div className={cx(classes.spacing, classes.divider)} />;
  }
  const gridTemplateColumns = `repeat(${items.length}, 1fr)`;

  return (
    <div className={classes.scrollable}>
      <div className={classes.supplyChain} style={{ gridTemplateColumns }}>
        {/* Headers */}
        {items.map((item, index) => (
          <SupplyChainHeader
            position={index}
            key={`header-${index}`}
            type={item.type}
            label={item.header.label}
            categoryName={item.header.categoryName}
            link={item.header.link}
            score={item.header.score}
          />
        ))}

        {/* Issues */}
        {items.map((item, index) => (
          <SupplyChainIssues
            key={`issues-${index}`}
            counters={item.issues.counters}
            label={item.issues.label}
            componentType={item.issues.componentType}
            className={cx(classes.spacing, classes.divider)}
            type={item.type}
            index={index}
          />
        ))}

        {/* Connectors */}
        {items.map((item, index) => (
          <React.Fragment key={`connectors-${index}`}>
            {item.type === SupplyChainItemType.Element && (
              <SupplyChainElementConnectors
                headerLabel={item.header.label}
                headerLink={item.header.link}
                footerLabel={item.issues.label?.text}
                footerLink={item.issues.label?.link}
                connectors={item.connectors?.items}
                showCustomIcon={!!item.connectors?.showCustomIcon}
                className={classes.divider}
              />
            )}
            {item.type === SupplyChainItemType.Category && (
              <SupplyChainToolConnectors
                index={index}
                connectors={item.connectors?.items}
                showCustomIcon={!!item.connectors?.showCustomIcon}
                className={cx(
                  classes.spacing,
                  classes.divider,
                  classes.stretch,
                )}
              />
            )}
          </React.Fragment>
        ))}

        {/* Coverage */}
        {haveCoverage &&
          items.map((item, index) => (
            <React.Fragment key={`coverage-${index}`}>
              {index === 0 && (
                <div
                  className={classes.coverage}
                  data-testid={`coverage-${index}`}>
                  <Typography variant='body1' color='text.primary'>
                    Coverage:
                  </Typography>
                </div>
              )}
              {item.type === SupplyChainItemType.Element && index > 0 && (
                <div data-testid={`coverage-${index}`} />
              )}
              {item.type === SupplyChainItemType.Category && (
                <SupplyChainCoverage coverage={item.coverage} index={index} />
              )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  scrollable: {
    overflowX: 'auto',
    width: '100%',
    height: '100%',
  },
  supplyChain: {
    display: 'grid',
    height: '100%',
    '--element-bg-color': lighten(theme.palette.background.default, 0.1),
    '& > div': {
      minWidth: 70,
    },
    '& a': {
      textDecoration: 'none',
    },
    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  spacing: {
    paddingBlock: theme.spacing(1),
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  stretch: {
    height: '100%',
  },
  coverage: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export interface SupplyChainProps {
  items: SupplyChainItem[];
  haveCoverage?: boolean;
}

export default SupplyChain;
